import { Switch as SwitchPrimitive } from "@base-ui/react/switch";
import { cn } from "cn";
import type * as React from "react";

interface Segment<T extends string> {
  value: T;
  label: React.ReactNode;
  /** A 14px icon before the label. */
  icon?: React.ReactNode;
  /** Hover text (e.g. why the control is disabled). */
  title?: string;
  testId?: string;
}

// The v3.2 segmented switch (the workspace pane's "Transcript | Assistant"):
// a sunken track with the selected segment raised on the panel colour.
// Tab semantics, with arrow keys moving between segments.
export function SegmentedControl<T extends string>({
  segments,
  value,
  onValueChange,
  label,
  stretch = false,
  disabled = false,
  className,
}: {
  segments: readonly Segment<T>[];
  value: T;
  onValueChange: (value: T) => void;
  /** Names the group for screen readers. */
  label: string;
  /** Full width, segments sharing it equally (a narrow panel's main nav). */
  stretch?: boolean;
  /** Every segment off, e.g. while a recording uploads. */
  disabled?: boolean;
  className?: string;
}): React.JSX.Element {
  const move = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    const step =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const index = segments.findIndex((segment) => segment.value === value);
    const next = segments[(index + step + segments.length) % segments.length];
    if (!next) return;
    onValueChange(next.value);
    event.currentTarget
      .querySelector<HTMLButtonElement>(`[data-value="${next.value}"]`)
      ?.focus();
  };
  return (
    <div
      aria-label={label}
      className={cn(
        "inline-flex h-9 shrink-0 items-center rounded-full bg-v3-surface-2 p-[3px] font-v3 shadow-v3-hairline",
        stretch && "flex w-full",
        className,
      )}
      role="tablist"
      onKeyDown={move}
    >
      {segments.map((segment) => {
        const selected = segment.value === value;
        return (
          <button
            key={segment.value}
            aria-selected={selected}
            className={cn(
              "h-[30px] rounded-full px-3.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:cursor-not-allowed disabled:opacity-50",
              selected
                ? "bg-v3-panel text-v3-text shadow-[0_1px_2px_rgba(0,0,0,0.08),0_0_0_1px_var(--v3-border)]"
                : "text-v3-text-2 enabled:hover:text-v3-text",
              (stretch || segment.icon) &&
                "inline-flex min-w-0 items-center justify-center gap-1.5 [&_svg]:size-3.5 [&_svg]:shrink-0",
              stretch && "flex-1",
            )}
            data-testid={segment.testId}
            data-value={segment.value}
            disabled={disabled}
            role="tab"
            tabIndex={selected ? 0 : -1}
            title={segment.title}
            type="button"
            onClick={() => onValueChange(segment.value)}
          >
            {segment.icon}
            {segment.icon || stretch ? (
              <span className="truncate">{segment.label}</span>
            ) : (
              segment.label
            )}
          </button>
        );
      })}
    </div>
  );
}

// The v3.2 switch (style.css .switch; iOS Toggle): ink when on, a sunken
// track when off, 44px touch target. Same look as apps/web's V3Switch.
export function Switch({
  className,
  ...props
}: SwitchPrimitive.Root.Props): React.JSX.Element {
  return (
    <SwitchPrimitive.Root
      className={cn(
        "peer relative inline-flex h-[26px] w-[44px] shrink-0 cursor-pointer items-center rounded-full p-[3px] transition-colors duration-200 before:absolute before:-inset-[9px] before:content-[''] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand data-disabled:cursor-not-allowed data-disabled:opacity-40 data-checked:bg-v3-ink data-unchecked:bg-v3-surface-3 data-unchecked:shadow-v3-hairline-strong",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="pointer-events-none block size-5 rounded-full bg-v3-panel shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-transform duration-200 ease-v3-ease data-checked:translate-x-[18px] data-unchecked:translate-x-0" />
    </SwitchPrimitive.Root>
  );
}

// A settings row: label and hint on the left, the switch on the right. The
// label names the switch; the hint describes it.
export function SwitchRow({
  id,
  label,
  hint,
  checked,
  onCheckedChange,
  disabled,
  testId,
}: {
  id: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  testId?: string;
}): React.JSX.Element {
  return (
    <div className="flex min-h-14 items-center gap-4 py-2">
      <div className="min-w-0 flex-1 font-v3">
        <label
          className="block cursor-pointer text-[15px] font-medium leading-5 text-v3-text"
          htmlFor={id}
          id={`${id}-label`}
        >
          {label}
        </label>
        {hint && (
          <p
            className="mt-0.5 text-[13px] leading-[18px] text-v3-text-3"
            id={`${id}-hint`}
          >
            {hint}
          </p>
        )}
      </div>
      <Switch
        aria-describedby={hint ? `${id}-hint` : undefined}
        aria-labelledby={`${id}-label`}
        checked={checked}
        data-testid={testId}
        disabled={disabled}
        id={id}
        onCheckedChange={(next) => onCheckedChange(next)}
      />
    </div>
  );
}
